import { CheckCircle, MoreVert, Notifications } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { ProcessExecutionAPI } from "../../../../api";
import TaskExecutionsAPI from "../../../../api/taskExecutions";
import RoleIcon from "../../../../components/elements/RoleIcon";
import { useGlobalRoleContext } from "../../../../hooks/useGlobalRoleContext";
import { useGlobalUserContext } from "../../../../hooks/useGlobalUserContext";
import { useAlert } from "../../../../lib/alert";
import { TaskExecution, TaskExecutionState } from "../../../task";
import { useProcessExecutionContext } from "../../hooks";
import TaskExecutionAccountModal from "./TaskExecutionAccountModal";
interface TaskOptionDropdownProps {
  taskExecution: TaskExecution;
  canBeAssigned: boolean;
}

const TaskOptionsButton = ({
  taskExecution,
  canBeAssigned,
}: TaskOptionDropdownProps) => {
  const hideRemindFeature = false;

  const user = useGlobalUserContext().user;
  const { roles } = useGlobalRoleContext();

  const notify = useAlert();

  const { getAssignedUser } = useProcessExecutionContext();
  const [assignedUser, setAssignedUser] = useState(
    getAssignedUser(taskExecution),
  );
  const [teaModalOpen, setTeaModalOpen] = useState<boolean>(false);
  const [sentReminder, setSentReminder] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  // Have to jump through some hoops to find out if global user has role
  const [canAssignSelf] = useState<boolean>(
    roles.some((r) => r.id == taskExecution.task?.role?.id),
  );

  const openTaskExecutionAccountModal = () => {
    setTeaModalOpen(true);
  };
  const closeTaskExecutionAccountModal = () => {
    setTeaModalOpen(false);
  };

  const assignToSelf = () => {
    ProcessExecutionAPI.assign(
      taskExecution.process_execution_id || "",
      taskExecution.task_id || "",
      user.id || "",
    )
      .then(() => {
        notify.success("Successfully assigned!");
      })
      .catch(() => {
        notify.error("Failed to assign!");
      });
    close();
  };

  const assignToOther = () => {
    openTaskExecutionAccountModal();
    close();
  };

  const remindUser = () => {
    setSending(true);
    // If reminder has not been set yet
    if (!sentReminder && !sending) {
      if (!taskExecution.id) {
        close();
        return;
      }

      TaskExecutionsAPI.remindTaskAccount(taskExecution.id)
        .then(() => {
          notify.success("Reminder Sent Successfully!");
          setSentReminder(true);
          setSending(false);
        })
        .catch((e) => {
          notify.error("Reminder Failed to Send!");
          console.error(e);
          setSending(false);
        });
    }
    close();
  };

  const FullDropdownButton = () => {
    const [anchor, setAnchor] = useState<null | HTMLElement>(null);
    const open = Boolean(anchor);

    const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchor(event.currentTarget);
    };

    const close = () => {
      setAnchor(null);
    };

    // Don't display if task role matches user role

    const matches = user.roles?.find(
      (role) => role.id == taskExecution.task?.role?.id,
    );

    return (
      <>
        <Button
          sx={{ mx: "10px" }}
          id="task-option-dropdown-button"
          variant="text"
          disabled={Boolean(taskExecution.completed_by_user_id)}
          aria-controls={open ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={onClick}
          startIcon={
            <RoleIcon
              role={taskExecution.task?.role}
              assignedUser={assignedUser}
              toolTipPosition="right"
            />
          }
          endIcon={!taskExecution.completed_by_user_id && <MoreVert />}
        ></Button>
        <Menu
          anchorEl={anchor}
          open={open}
          onClose={close}
          MenuListProps={{ "aria-labelledby": "task-option-dropdown-button" }}
        >
          {
            // Always allow user to remind assigned role once
            !hideRemindFeature &&
              taskExecution.task?.previous_task_id != undefined &&
              !matches &&
              taskExecution.state == TaskExecutionState.InProgress && (
                <MenuItem onClick={remindUser}>
                  Remind {taskExecution.task?.role?.name || "Next User"}
                </MenuItem>
              )
          }
          {
            !sentReminder && !hideRemindFeature && !matches && <Divider /> // Must be seperate or else MUI will throw an error
          }
          {canBeAssigned && (
            <MenuItem onClick={assignToOther}>Assign To Someone</MenuItem>
          )}
          {canBeAssigned && canAssignSelf && (
            <MenuItem onClick={assignToSelf}>Assign To Myself</MenuItem>
          )}
        </Menu>
        <TaskExecutionAccountModal
          open={teaModalOpen}
          handleClose={closeTaskExecutionAccountModal}
          taskExecution={taskExecution}
          assignedUser={assignedUser}
          setAssignedUser={setAssignedUser}
        />
      </>
    );
  };

  const SimpleRemindIcon = () => {
    // Don't display if task role matches user role
    const matches = user.roles?.find(
      (role) => role.id == taskExecution.task?.role?.id,
    );

    return (
      !hideRemindFeature &&
      taskExecution.task?.previous_task_id != undefined &&
      !matches &&
      taskExecution.state == TaskExecutionState.InProgress && (
        <Tooltip
          title={
            (!sentReminder &&
              `Remind ${taskExecution.task?.role?.name || "Next User"}`) ||
            "Reminder Sent!"
          }
        >
          <IconButton onClick={remindUser}>
            {(!sentReminder && !sending && <Notifications />) || ( // Not yet sent
              <CheckCircle /> // Sent
            )}
          </IconButton>
        </Tooltip>
      )
    );
  };

  /*
   * If can be assigned, use full dropdown UI
   * Otherwise, show just a simple remind button if not yet reminded
   */
  return (
    <Box sx={{ display: "flex", alignItems: "flex-end" }}>
      {canBeAssigned && <FullDropdownButton />}
      {!canBeAssigned && <SimpleRemindIcon />}
    </Box>
  );
};

export default TaskOptionsButton;
